import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Empty, ScreenNote, TableWarningModal, Button, ScreenSearch, Breadcrumbs, Table2 } from "components";
import { toastAlert } from "@paylixecommerce/shared/components";
import { Link, Route, Switch, useHistory } from "react-router-dom";
import { getBundles, deleteBundle } from "./actions";
import moment from "moment";
import { search } from "../product/index";
import roleHOC from "HOC/roleHOC";
import config from "constants/config";
import { DotsIcon, TableDelete, TableEdit } from "../../../icons";
import CreateUpselling from "./screens/create";
import EditUpselling from "./screens/details";
import {random} from "../../comms/appeal";
import useMobile from "../../../../../hooks/useMobile";
import MobileProducts from "./mobile-products";
import MobilePlaceholder from "./mobile-placeholder";

import "./style.scss";

export const Actions = ({ data, showConfirm }) => {
	return (
		<div className="paylixecommerce-table-actions">
			<DotsIcon />

			<div className="paylixecommerce-table-actions-modal">
				<Link to={`/upselling/edit/${data.uniqid}`}>
					<TableEdit />
					<span>Edit</span>
				</Link>
				<div onClick={() => showConfirm(data)} className="delete-icon">
					<TableDelete />
					<span>Delete</span>
				</div>
			</div>
		</div>
	)
};


const Upselling = () => {

	const [loading, setLoading] = useState(true);
	let [bundles, setBundles] = useState([]);
	const [searchKey, setSearch] = useState("");
	const [confirm, showConfirm] = useState(null);
	const name = useSelector(({ auth }) => auth.shop ? auth.shop.name : '');
	const currency = useSelector(({ auth }) => (auth.shop || {}).currency);
	const dispatch = useDispatch();
	const history = useHistory();
	const isMobile = useMobile();

	useEffect(() => {
		dispatch(getBundles(name))
			.then(res => setBundles(res.data.bundles))
			.catch(err => toastAlert('error', err.error || 'Something went wrong!'))
			.finally(() => setLoading(false))
	}, [name, history.location.pathname]);

	const deleteBundleAction = (id) => {
		setLoading(true);
		dispatch(deleteBundle(id))
			.then(res => {
				dispatch(getBundles(name))
					.then(res => setBundles(res.data.bundles))
					.catch(err => toastAlert('error', err.error || 'Something went wrong!'));

					toastAlert('success', res.message);
			})
			.catch(err => toastAlert('error', (err && err.error) || 'Something went wrong!'))
			.finally(() => {
				showConfirm(null)
				setLoading(false)
			})
	}

	let isEmpty = !loading && !bundles.length;

	bundles = searchKey ? search(bundles, ['title'], searchKey) : bundles;

	const columns = [{
		accessor: 'title',
		Header: 'Name'
	},  {
		accessor: 'discount_amount',
		Header: 'Discount',
		Cell: ({ row }) => row.original.discount_type === "FIXED" ? `${config.CURRENCY_LIST[currency]}${Number(row.original.discount_amount).toFixed()}` : `${Number(row.original.discount_amount).toFixed()}%`,
	}, {
		accessor: 'id',
		Header: 'ID'
	},{
		accessor: 'products',
		Header: 'Products',
		Cell: ({ row }) => `${row.original.products.split(',').length}`,
	}, {
		accessor: 'updated_at',
		Header: 'Last Update',
		Cell: ({ row }) => moment(row.original.updated_at * 1000).format("YYYY DD, MMM HH:mm")
	}, {
		id: 'id2',
		Header: '',
		Cell: ({ row }) => <Actions showConfirm={() => showConfirm(row.original)} data={row.original} />
	}];


	const placeholder = [{
		accessor: 'title',
		Header: 'Name',
		Cell: () => <div className="skeleton skeleton1" style={{ width: random(20, 200) }} />
	}, {
		accessor: 'discount_amount',
		Header: 'Discount',
		Cell: () => <div className="skeleton skeleton2" style={{ width: random(20, 50) }} />
	}, {
		accessor: 'id',
		Header: 'ID',
		Cell: () => <div className="skeleton skeleton3" style={{ width: random(20, 200) }} />
	}, {
		accessor: 'products',
		Header: 'Products',
		Cell: () => <div className="skeleton skeleton4" style={{ width: random(20, 50) }} />
	}, {
		accessor: 'updated_at',
		Header: 'Last Update',
		Cell: () => <div className="skeleton skeleton7" style={{ width: random(100, 200) }} />
	}, {
		id: 'id2',
		Header: '',
		Cell: ({ row }) => <Actions showConfirm={() => showConfirm(row.original)} data={row.original} />
	}];

	return (
		<div className="upselling-screen">

			<TableWarningModal
				close={() => showConfirm(null)}
				open={confirm}
				deleteAction={deleteBundleAction}
				type="bundle"
				isLoading={loading}
			/>

			{isEmpty ? null : <div className="screen-header">
				<Breadcrumbs title="Upselling" breadcrumbs={[{ title: 'Products', link: '/' }, { title: 'Upselling', link: '/upselling' }]} />

				<div className="screen-filters">
					<ScreenSearch className="long" onChange={setSearch} search={searchKey} />
					<Button iconName="plus" iconPosition="left" size="large" onClick={() => history.push(`/upselling/new`)}>
						Create
					</Button>
				</div>
			</div>}

			{isEmpty ? null : <ScreenNote name="showUpsellingNote"
				note={<>
					Product bundles are like amazon: <i> Would you like to add <b>'coffee cup'</b> to your cart and get a <b>30$</b> discount? Do it now!</i> <br/>
					So it's another form of discount for the shopping cart only and that will be displayed when buying a product with like suggestions for stores.
				</>}
			/>}

			{isEmpty ? <Empty isBundle /> : null}

			{isMobile ? null : loading ?
				<Table2 data={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]} isPlaceholder columns={placeholder} /> :
				isEmpty ? null :
				<Table2 data={bundles} columns={columns} />
			}
			{isMobile ? loading ?
				<MobilePlaceholder data={[1, 2, 3]} /> :
				isEmpty ? null :
				<MobileProducts currency={currency} data={bundles} columns={columns} />
			: null}


			<Switch>
				<Route exact={true} path="/upselling/new">
					<>
						<div className="edit-modal-overlay" onClick={() => history.push("/upselling")} />
						<div className="edit-modal padding">
							<CreateUpselling />
						</div>
					</>
				</Route>
				<Route exact={true} path="/upselling/edit/:id">
					<>
						<div className="edit-modal-overlay" onClick={() => history.push("/upselling")} />
						<div className="edit-modal padding">
							<EditUpselling />
						</div>
					</>
				</Route>
			</Switch>


		</div>
	)
}

export default roleHOC(Upselling);

